// Question content with its placeholders made real.
//
// LCMS authors content in a rich-text editor that stores some things as empty custom spans. They
// carry all their meaning in data attributes, so a browser renders them as NOTHING - which is
// how a listening question arrived silent and a cloze question arrived with its gaps missing
// from the sentence. This turns each one back into something a child can see and use.
//
// LCMS stores a clip as a placeholder inside the content HTML:
//
//   <span data-type="audio-node" data-audio-id="..." data-file-name="..." data-label="..."></span>
//
// which renders as nothing at all on its own - so a listening question used to arrive silent.
// The bytes are in the database rather than on a CDN and the endpoint needs the student's token,
// so a plain <audio src> cannot reach them either.
//
// This renders the content as HTML, then finds those placeholders and turns each into a play
// button that fetches the clip on first press and keeps it for later presses.

import React, { useCallback, useEffect, useRef } from "react"
import { useEngAudioFetcher } from "./eng-diag-audio-context"
import styles from "./eng-diag-question-view.module.css"

interface EngDiagRichContentProps {
  html: string
  className?: string
  /**
   * Gap id -> what the student has put there: the word picked for a cloze, or the text typed
   * into a blank. Shown inside the gap so the sentence reads back as they have built it.
   */
  gapFills?: Record<string, string>
}

/** Label under the button. The author's own label wins; otherwise something a child can read. */
function buttonLabel(node: HTMLElement): string {
  return (node.dataset.label || "").trim() || "Listen"
}

/**
 * The widest an authored answer gap is allowed to get, in characters.
 *
 * Wide enough to read as "something long goes here", narrow enough to sit inside the card on a
 * tablet. See the note where it is used.
 */
const GAP_MAX_CHARS = 24

export const EngDiagRichContent: React.FC<EngDiagRichContentProps> = ({
  html,
  className,
  gapFills,
}) => {
  // Diagnostics or Skill Builder - the two clips live behind different endpoints. See
  // EngAudioFetcherContext.
  const fetchAudio = useEngAudioFetcher()
  const hostRef = useRef<HTMLDivElement | null>(null)
  // Object URLs per clip, so pressing play twice does not fetch twice. Revoked on unmount.
  const urlsRef = useRef<Map<string, string>>(new Map())
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const play = useCallback(async (audioId: string, button: HTMLButtonElement) => {
    const existing = audioRef.current
    // One clip at a time: two talking at once is unusable, and a child will press everything.
    if (existing) {
      existing.pause()
      existing.currentTime = 0
    }

    try {
      let url = urlsRef.current.get(audioId)
      if (!url) {
        button.disabled = true
        button.dataset.state = "loading"
        const blob = await fetchAudio(audioId)
        url = URL.createObjectURL(blob)
        urlsRef.current.set(audioId, url)
      }

      const audio = new Audio(url)
      audioRef.current = audio
      button.dataset.state = "playing"
      audio.onended = () => {
        button.dataset.state = "idle"
      }
      audio.onerror = () => {
        button.dataset.state = "error"
      }
      await audio.play()
    } catch {
      // A child can do nothing about a failed fetch, so this only marks the button rather than
      // interrupting the question. They can still answer, and can press again.
      button.dataset.state = "error"
    } finally {
      button.disabled = false
    }
  }, [fetchAudio])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const placeholders = Array.from(
      host.querySelectorAll<HTMLElement>('[data-type="audio-node"]'),
    )

    const cleanups: Array<() => void> = []
    for (const node of placeholders) {
      const audioId = (node.dataset.audioId || "").trim()
      if (!audioId) continue

      const button = document.createElement("button")
      button.type = "button"
      button.className = styles.engDiagAudioButton
      button.dataset.state = "idle"
      // Spoken by a screen reader, and shown if the icon font fails.
      button.setAttribute("aria-label", `Play audio: ${buttonLabel(node)}`)
      button.innerHTML =
        `<span aria-hidden="true">&#9654;</span><span>${buttonLabel(node)}</span>`

      const onClick = (event: Event) => {
        event.preventDefault()
        // A clip can sit INSIDE a multiple-choice option, which is itself clickable. Without
        // this, pressing play would also pick that option - so a child listening through the
        // four choices would answer with whichever they happened to hear last.
        event.stopPropagation()
        void play(audioId, button)
      }
      button.addEventListener("click", onClick)
      cleanups.push(() => button.removeEventListener("click", onClick))

      node.replaceChildren(button)
    }

    return () => cleanups.forEach(fn => fn())
  }, [html, play])

  // Separate from the audio effect, and keyed on the answers: filling a gap only rewrites the
  // text inside it, so the audio buttons above are not torn down and rebuilt on every click.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    // Both kinds of gap: a cloze blank the student picks a word for, and the placeholder a
    // `blank` question puts where the typed answer belongs. Same shape, same treatment.
    for (const gap of Array.from(
      host.querySelectorAll<HTMLElement>(
        '[data-type="mc-cloze-blank"], [data-type="answer-placeholder"]',
      ),
    )) {
      const id = (gap.dataset.id || "").trim()
      const filled = (gapFills || {})[id] || ""
      gap.className = filled ? styles.engDiagClozeGapFilled : styles.engDiagClozeGap
      // A gap with nothing in it still has to occupy space, or the sentence closes up and the
      // child cannot see there is something missing. Sized by the author's character count,
      // CAPPED: the count is the length of the expected answer, not a width, and the bank holds
      // gaps asking for 100 and even 750 characters. A min-width that big cannot wrap, so the
      // dashed box ran straight out of the card and off the screen - which is what the PM
      // photographed on the draft-sentence question (data-char-count="100").
      //
      // Only 5 of the 183 authored gaps are over this, and for those the box is a place to look
      // rather than a measure of what to write - the answer is typed into the control below it.
      const width = Number(gap.dataset.charCount) || 6
      gap.style.minWidth = `${Math.min(Math.max(3, width), GAP_MAX_CHARS)}ch`
      gap.textContent = filled
    }
  }, [html, gapFills])

  useEffect(() => {
    const urls = urlsRef.current
    return () => {
      audioRef.current?.pause()
      urls.forEach(url => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  return (
    <div
      ref={hostRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
